import type { HelpContent } from '../types'

/**
 * Help panel + first-run walkthrough copy for the Leads (pipeline) section.
 * Keep every string em-dash free. Do not describe controls that do not yet
 * work as if they do (the connection-degree badge and mutual-connection count
 * are not wired to real data, so they are intentionally left out).
 */
export const LEADS_HELP: HelpContent = {
  key: 'leads',
  title: 'Leads',
  intro:
    'Leads is your prospect pipeline. Every person the system can reach lives here and moves through stages from first contact toward a booked meeting. Most moves happen automatically, and you step in for the calls that need a human.',
  panel: [
    {
      heading: 'How it works',
      items: [
        {
          text: 'Create a campaign first, then add prospects with Add Lead (search for new people) or Add with URL (paste a LinkedIn profile or a people-search page) and assign them to it.',
        },
        {
          text: 'The system sends a connection request, then a first message once it is accepted, then spaced follow-ups if there is no reply.',
        },
        {
          text: 'Sending happens on weekdays between 9:00 AM and 5:00 PM New York time. Weekends are paused by default; switch on weekend activity in Settings, Safety if you want it. Daily volume is managed for you within LinkedIn safe limits.',
        },
        { text: 'As a lead responds, it advances through the stages on its own.' },
        {
          text: 'You drag a card by hand only for the human milestones: Replied to Meeting Booked, then to Won or Lost.',
        },
        {
          text: 'Click any lead to research it, read the full conversation, and see its timeline.',
        },
      ],
    },
    {
      heading: 'Two views',
      items: [
        {
          term: 'Kanban',
          text: 'A stage board. Each column is a stage, and every card sits in the stage it has reached.',
        },
        {
          term: 'Table',
          text: 'The same leads as a sortable, filterable list. Click a column header to sort by it.',
        },
      ],
    },
    {
      heading: 'What the stages mean',
      items: [
        { term: 'Discovered', text: 'New matches you added or the system found, not yet contacted.' },
        { term: 'Request Sent', text: 'A connection invite went out and you are waiting for it to be accepted.' },
        { term: 'Connected', text: 'The invite was accepted, and the first message sends automatically.' },
        { term: 'Messaged', text: 'The first message has been sent and follow-ups are scheduled.' },
        {
          term: 'Follow-up 1, 2, 3',
          text: 'Automated nudges that go out in order when there is no reply. After the last one, no more messages send unless the lead replies.',
        },
        { term: 'Replied', text: 'The lead answered. Drag them to Meeting Booked once a meeting is agreed.' },
        { term: 'Meeting Booked', text: 'You drag a Replied card here yourself once a meeting is agreed. That stops automation for the lead, because you take over.' },
        { term: 'Won or Lost', text: 'You drag leads here yourself to close them out. Automation stays stopped for those leads.' },
      ],
    },
    {
      heading: 'Research (enrichment)',
      items: [
        {
          text: 'Fields like company and industry stay locked until you research a lead. Research uses AI to pull company details, recent news, likely pain points, and a suggested outreach angle.',
        },
        {
          text: 'Each research run costs one credit. Research a single lead from its detail panel, or import a lead with research turned on. Research and enrich are the same action.',
        },
      ],
    },
    {
      heading: 'Working in bulk',
      items: [
        {
          text: 'Turn on Select to show checkboxes, then add many leads to a campaign, change their stage, or delete them at once.',
        },
        {
          text: 'Filter the pipeline by name, company, or title, or narrow it to a single campaign.',
        },
      ],
    },
    {
      heading: 'The lead detail panel',
      items: [
        { term: 'Overview', text: 'A quick summary: company, industry, suggested strategy, and top fit signals.' },
        { term: 'Research', text: 'The full AI writeup on the company and how best to reach out.' },
        { term: 'Conversation', text: 'Every message sent to and received from this lead.' },
        { term: 'Timeline', text: 'A dated history of key events for the lead.' },
      ],
    },
    {
      heading: 'Good to know',
      items: [
        {
          text: 'Messaging a lead yourself pauses automation for that lead, so the system never talks over you.',
        },
        { text: 'A lead can belong to one campaign at a time.' },
        { text: 'Fit signals (in Research) are reasons a lead matches your profile. Timeline events are dated actions. They are different things.' },
      ],
    },
  ],
  tour: [
    {
      id: 'welcome',
      title: 'Your lead pipeline',
      body: 'This is where every prospect lives. The system moves leads from first contact toward a booked meeting on its own, and you step in for the moves that need a human.',
    },
    {
      id: 'stats',
      target: 'leads-stats',
      title: 'Your pipeline at a glance',
      body: 'These four numbers track the pipeline: total leads, how many you added this week, your reply rate, and your win rate.',
    },
    {
      id: 'views',
      target: 'leads-view-toggle',
      title: 'Two ways to look',
      body: 'Switch between the Kanban stage board and a sortable Table of the same leads. Your choice is remembered.',
    },
    {
      id: 'stages',
      target: 'leads-board',
      title: 'Stages move on their own',
      body: 'Each column is a stage, and leads advance automatically as they connect and reply. You drag a card by hand only for the closing moves: Replied to Meeting Booked, then Won or Lost. Dragging a lead into those stages stops automation for it, because you take over. Click the question mark on any column heading for details.',
    },
    {
      id: 'schedule',
      title: 'When messages go out',
      body: 'Outreach sends on weekdays between 9:00 AM and 5:00 PM New York time, so your messages land during business hours. Nothing sends on Saturday or Sunday unless you switch on weekend activity in Settings, Safety. If a campaign looks quiet at night or over a weekend, that is the schedule working, not a problem.',
    },
    {
      id: 'research',
      title: 'Research a lead',
      body: 'Some fields stay locked until you research a lead. Research pulls company details, news, and a suggested angle using AI, and costs one credit.',
    },
    {
      id: 'filters',
      target: 'leads-filters',
      title: 'Find specific leads',
      body: 'Filter the pipeline by name, company, or title, or narrow it to a single campaign.',
    },
    {
      id: 'add',
      target: 'leads-add',
      title: 'Add more prospects',
      body: 'Add Lead opens search to find new people who fit your profile. Add with URL takes a LinkedIn profile link or a whole people-search page. Once added, put them in a campaign so outreach can start.',
    },
    {
      id: 'bulk',
      target: 'leads-select',
      title: 'Work in bulk',
      body: 'Turn on Select to check many leads at once, then add them to a campaign, change their stage, or delete them together.',
    },
    {
      id: 'detail',
      title: 'Open any lead',
      body: 'Click a lead to see its research, full conversation, and timeline. That is the whole pipeline. Reopen this walkthrough anytime from the Help button.',
    },
  ],
}
